import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import * as moment from 'moment';
import { HttpService } from 'src/modules/shared/services/http.service';
import { Router } from '@angular/router';
import { PopupService } from 'src/modules/shared/services/popup.service';
import { Location } from '@angular/common';
import { IUser } from 'src/modules/authentication/interfaces/user.interface';
import { SharedDataService } from 'src/modules/shared/services/shared-data.service';

type IdCheckStatus =
  | 'required'
  | 'awaiting_feedback'
  | 'approved'
  | 'not_required'
  | undefined;

@Component({
  selector: 'app-id-check',
  templateUrl: './id-check.component.html',
  styleUrls: ['./id-check.component.scss'],
  standalone: false
})
export class IdCheckComponent implements OnInit {
  form = new FormGroup({
    email: new FormControl({ value: '', disabled: true }, [
      Validators.required,
    ]),
  });

  isSubmitting = false;
  status: IdCheckStatus = 'required'; // default

  get email() {
    return this.form.get('email');
  }
  get isPending() {
    return this.status === 'awaiting_feedback';
  }
  get isApproved() {
    return this.status === 'approved';
  }

  constructor(
    private httpService: HttpService,
    private router: Router,
    private popupService: PopupService,
    private location: Location,
    private sharedDataService: SharedDataService
  ) {}

  async ngOnInit() {
    let currentUser = this.sharedDataService.currentUser;

    // If user not available in shared service, try to fetch from API
    if (!currentUser) {
      try {
        currentUser = await this.httpService.httpGetRequest<IUser>('/auth/employee/me');
        // Store in shared service for future use
        this.sharedDataService.currentUser = currentUser;
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        // If API call fails (e.g., user not authenticated), redirect to home
        this.router.navigateByUrl('/home', { replaceUrl: true });
        return;
      }
    }

    // Process user data
    if (currentUser) {
      this.email?.setValue(currentUser.email);
      const s = (currentUser as any).identityVerificationCheckStatus as IdCheckStatus;
      if (s) this.status = s;
    } else {
      // If no user data available, redirect to home
      this.router.navigateByUrl('/home', { replaceUrl: true });
    }
  }

  goBack() {
    this.location.back();
  }

  async submit() {
    // Only used when status is not pending/approved.
    try {
      this.isSubmitting = true;
      const nowIso = moment().toISOString();
      const email = this.form.getRawValue().email;

      await this.httpService.httpPutRequest('/employees/id-verification', {});

      // If it was required, move UI to pending locally (optional)
      if (this.status !== 'approved') this.status = 'awaiting_feedback';
      this.router.navigateByUrl('/home');
    } catch (err: any) {
      if (err?.error?.message)
        this.popupService.showErrorModal(err.error.message);
      console.error(err);
    } finally {
      this.isSubmitting = false;
    }
  }
}
